import { Inject, Injectable } from '@angular/core';
import { Dialog, DialogRef } from '@angular/cdk/dialog';
import { ComponentType } from '@angular/cdk/portal';
import { ActivationStart, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { NoopScrollStrategy } from '@angular/cdk/overlay';

export interface DialogData extends Record<string, unknown> {
  className?: string;
  autoCloseOnRouteChange?: boolean;
  disableClose?: boolean;
  hasBackdrop?: boolean;
  autoFocus?: string;
  panelClass?: string;
}
/**
 * @deprecated Use DrawerService instead. This will be made internal once
 * all modals are converted to drawers.
 */
@Injectable()
export class DialogService {
  public activeDialogs: Map<ComponentType<unknown>, DialogRef> = new Map();
  public routeWatchers: Map<ComponentType<unknown>, Subscription> = new Map();
  private rootEl: Element;
  private bodyEl: Element;

  constructor(
    private dialog: Dialog,
    private router: Router,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.rootEl = this.document.getElementsByTagName('dgx-root')[0];
    this.bodyEl = this.document.getElementsByTagName('body')[0];
  }

  /**
   * Open dialog with component as content... close existing dialog if open
   */
  public show<T extends DialogData>(
    component: ComponentType<unknown>,
    data?: T
  ): DialogRef {
    const isOpen = this.activeDialogs.has(component);
    if (isOpen) this.close(component); // If the same component dialog is already open, close it

    return this.open(component, data);
  }

  /**
   * Close a specific dialog or all dialogs if no component specified
   * NOTE: Easily close all dialogs with `close()` or `closeAll()`
   */
  public close(component?: ComponentType<unknown>, result?: any): void {
    const notFound = () =>
      console.warn(`No dialog found for ${component.name}`);
    const dialogRef = this.activeDialogs.get(component);

    if (!component) return this.closeAll();
    if (!dialogRef) return notFound();

    // close the modal
    dialogRef.close(result);
    this.clearRegistration(component);
  }

  /**
   * Close all dialogs
   */
  public closeAll(): void {
    // close any modals
    this.activeDialogs.forEach(
      (_, component) => component && this.close(component)
    );
  }

  // ******************************************
  // Private Methods
  // ******************************************

  /**
   * Open dialog with component as content
   */
  private open<T extends DialogData>(
    component: ComponentType<unknown>,
    data?: T
  ): DialogRef {
    // set the main container to inert to prevent keyboard interaction
    this.rootEl.setAttribute('inert', '');

    // prevent scrolling on the body while the modal is open
    this.bodyEl.classList.add('tw-overflow-y-hidden');

    // open the modal
    const dialogRef = this.dialog.open(component, {
      width: data?.isFullWidth ? '100%' : 'auto',
      hasBackdrop: data?.hasBackdrop ?? true,
      backdropClass:
        (data?.hasBackdrop ?? true) ? 'apollo-overlay-dark-backdrop' : '',
      // TODO: I don't think this does anything to add the className.
      // there's a panelClass option to add to the panel classes, which I can do but
      // the tailwind doesn't seem to be necessary here
      data: { className: 'tw-max-w-xl', ...data },
      panelClass: data?.panelClass,
      disableClose: !!data?.disableClose,
      ariaModal: data?.hasBackdrop ?? true,
      autoFocus: data?.autoFocus ?? 'first-heading',
      // we need to handle scroll blocking specially because our nesting is not using html
      // so BlockScrollStrategy adds the `cdk-global-scrollblock` to html which causes
      // some issues-- we're using `tw-overflow-y-hidden` on body instead
      scrollStrategy: new NoopScrollStrategy(),
    });

    return this.addToRegistration(dialogRef, component, data);
  }

  /**
   * Cache dialog instance and optionally auto-close on route change
   */
  private addToRegistration<T extends DialogData>(
    dialogRef: DialogRef,
    component: ComponentType<unknown>,
    data?: T
  ): DialogRef {
    this.activeDialogs.set(component, dialogRef);

    // Optionally auto-close Dialog isntance when the route changes
    if (data?.autoCloseOnRouteChange) {
      const isRouteChange = (event: unknown): event is ActivationStart =>
        event instanceof ActivationStart;
      const routeWatcher = this.router.events.subscribe(
        (event) => isRouteChange(event) && this.close(component)
      );

      this.routeWatchers.set(component, routeWatcher);
    }

    // Clear from active registry when dialog is closed
    dialogRef.closed.subscribe(() => this.clearRegistration(component));

    return dialogRef;
  }

  /**
   * Clear from active registry when dialog is closed
   */
  private clearRegistration(component: ComponentType<unknown>): void {
    this.activeDialogs.delete(component);

    // remove inert from the main container to allow interaction again
    this.rootEl.removeAttribute('inert');

    // allow scrolling on body again
    this.bodyEl.classList.remove('tw-overflow-y-hidden');

    // Disconnect auto-close on route change when dialog is closed
    this.routeWatchers.get(component)?.unsubscribe();
    this.routeWatchers.delete(component);
  }
}
